import React, { useCallback, useEffect, useState } from 'react';
import { Button, HStack, Divider, Text, Flex, VStack } from 'native-base';
import { ModalType, useModalsContext } from '../../hooks/useModalsContext';
import { useFoodList } from '../../hooks/useFoodList';
import { Loader } from '../Loader';

export const FoodRestrictions = ({
  onChange,
  allergies = [],
  restrictions = [],
  avoided = [],
  other = [],
  variant = 'drawer',
}) => {
  const { getAllFoods, isFetchingFood } = useFoodList();
  const { dispatch } = useModalsContext();
  const selectedAllergies = allergies;
  const selectedRestrictions = restrictions;
  const selectedAvoided = avoided;
  const selectedOther = other;

  const [foodLists, setFoodLists] = useState(null);

  const onCloseModal = useCallback(() => {
    dispatch({ type: 'closeModal' });
  }, [dispatch]);

  const onFoodChange = useCallback((foodKey, foodList) => {
    onChange({
      allergies: selectedAllergies,
      restrictions: selectedRestrictions,
      avoided: selectedAvoided,
      other: selectedOther,
      [foodKey]: foodList, // override the one changed
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function truncate(input) {
    if (input?.length > 24) {
      return input.substring(0, 24) + '...';
    }
    return input;
  }

  useEffect(() => {
    const fetchAllFoods = async () => {
      if (!foodLists) {
        const fetchedFood = await getAllFoods();
        setFoodLists(fetchedFood);
      }
    };
    fetchAllFoods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isFetchingFood) return <Loader loading={isFetchingFood} />;

  return (
    <>
      <Text textAlign="left" fontWeight={600}>
        Allergies
      </Text>
      <HStack
        w={'100%'}
        justifyContent={'space-between'}
        alignItems={'flex-start'}
      >
        {selectedAllergies.length > 0 && (
          <Flex
            flexDir={'row'}
            flexBasis={variant === 'drawer' && '75%'}
            flexWrap={'wrap'}
            maxW={variant !== 'drawer' && '80%'}
          >
            {selectedAllergies.map((item, index) => {
              return (
                <Text
                  key={index}
                  px={4}
                  mr={2}
                  mt={2}
                  py={1}
                  borderWidth={'1px'}
                  borderColor={'muted.500'}
                  borderRadius={4}
                  color={'muted.500'}
                  fontWeight={'normal'}
                >
                  {truncate(
                    foodLists?.['allergens']?.find(
                      ({ ayble_id }) => item === ayble_id
                    )?.display_name
                  )}
                </Text>
              );
            })}
          </Flex>
        )}

        {selectedAllergies.length === 0 && (
          <VStack justifyContent={'center'} alignItems={'center'} pt={4}>
            <Text fontSize={'md'} italic>
              None
            </Text>
          </VStack>
        )}

        <Button
          variant={'link'}
          onPress={() =>
            dispatch({
              type: 'openModal',
              modalId: ModalType.EditFoodListModal,
              modalData: {
                foodList: foodLists?.['allergens'],
                onSave: (foodList) => onFoodChange('allergies', foodList),
                onClose: onCloseModal,
                initialSelectedFood: selectedAllergies,
                title: 'Food Allergies',
                subtitle: 'Select all that apply.',
              },
            })
          }
        >
          Edit
        </Button>
      </HStack>

      {variant === 'drawer' && <Divider my={4} />}

      <Text textAlign="left" fontWeight={600}>
        Diet
      </Text>
      <HStack
        w={'100%'}
        flex={1}
        justifyContent={'space-between'}
        alignItems={'flex-start'}
      >
        {selectedRestrictions.length > 0 && (
          <Flex
            flexBasis={variant === 'drawer' && '75%'}
            flexWrap={'wrap'}
            flexDir={'row'}
            maxW={variant !== 'drawer' && '80%'}
          >
            {selectedRestrictions.map((item, index) => {
              return (
                <Text
                  key={index}
                  px={4}
                  mr={2}
                  mt={2}
                  py={1}
                  borderWidth={'1px'}
                  borderColor={'muted.500'}
                  borderRadius={4}
                  color={'muted.500'}
                  fontWeight={'normal'}
                >
                  {truncate(
                    foodLists?.['dietary_restrictions']?.find(
                      ({ ayble_id }) => item === ayble_id
                    )?.display_name
                  )}
                </Text>
              );
            })}
          </Flex>
        )}

        {selectedRestrictions.length === 0 && (
          <VStack justifyContent={'center'} alignItems={'center'} pt={4}>
            <Text fontSize={'md'} italic>
              None
            </Text>
          </VStack>
        )}

        <Button
          variant={'link'}
          onPress={() =>
            dispatch({
              type: 'openModal',
              modalId: ModalType.EditFoodListModal,
              modalData: {
                foodList: foodLists?.['dietary_restrictions'],
                onSave: (foodList) => onFoodChange('restrictions', foodList),
                onClose: onCloseModal,
                initialSelectedFood: selectedRestrictions,
                title: 'Food Restrictions',
                subtitle: 'Select all that apply.',
              },
            })
          }
        >
          Edit
        </Button>
      </HStack>

      {variant === 'drawer' && <Divider my={4} />}

      <Text textAlign="left" fontWeight={600}>
        Avoided Foods
      </Text>
      <HStack
        w={'100%'}
        flex={1}
        justifyContent={'space-between'}
        alignItems={'flex-start'}
      >
        {selectedAvoided.length > 0 && (
          <Flex
            flexBasis={variant === 'drawer' && '75%'}
            flexWrap={'wrap'}
            flexDir={'row'}
            maxW={variant !== 'drawer' && '80%'}
          >
            {selectedAvoided.map((item, index) => {
              return (
                <Text
                  key={index}
                  px={4}
                  mr={2}
                  mt={2}
                  py={1}
                  borderWidth={'1px'}
                  borderColor={'muted.500'}
                  borderRadius={4}
                  color={'muted.500'}
                  fontWeight={'normal'}
                >
                  {truncate(
                    foodLists?.['avoided_foods']?.find(
                      ({ ayble_id }) => item === ayble_id
                    )?.display_name
                  )}
                </Text>
              );
            })}
          </Flex>
        )}

        {selectedAvoided.length === 0 && (
          <VStack justifyContent={'center'} alignItems={'center'} pt={4}>
            <Text fontSize={'md'} italic>
              None
            </Text>
          </VStack>
        )}

        <Button
          variant={'link'}
          onPress={() =>
            dispatch({
              type: 'openModal',
              modalId: ModalType.EditFoodListModal,
              modalData: {
                foodList: foodLists?.['avoided_foods'],
                onSave: (foodList) => onFoodChange('avoided', foodList),
                onClose: onCloseModal,
                initialSelectedFood: selectedAvoided,
                title: 'Avoided Foods',
                subtitle:
                  'Consumed less than once a week. Select all that apply.',
              },
            })
          }
        >
          Edit
        </Button>
      </HStack>

      {variant === 'drawer' && <Divider my={4} />}

      <Text textAlign="left" fontWeight={600}>
        Others
      </Text>
      <HStack
        w={'100%'}
        flex={1}
        justifyContent={'space-between'}
        alignItems={'flex-start'}
      >
        {selectedOther.length > 0 && (
          <Flex
            flexBasis={variant === 'drawer' && '75%'}
            flexWrap={'wrap'}
            flexDir={'row'}
            maxW={variant !== 'drawer' && '80%'}
          >
            {selectedOther.map((item, index) => {
              return (
                <Text
                  key={index}
                  px={4}
                  mr={2}
                  mt={2}
                  py={1}
                  borderWidth={'1px'}
                  borderColor={'muted.500'}
                  borderRadius={4}
                  color={'muted.500'}
                  fontWeight={'normal'}
                >
                  {truncate(
                    foodLists?.['other_diets']?.find(
                      ({ ayble_id }) => item === ayble_id
                    )?.display_name
                  )}
                </Text>
              );
            })}
          </Flex>
        )}

        {selectedOther.length === 0 && (
          <VStack justifyContent={'center'} alignItems={'center'} pt={4}>
            <Text fontSize={'md'} italic>
              None
            </Text>
          </VStack>
        )}

        <Button
          variant={'link'}
          onPress={() =>
            dispatch({
              type: 'openModal',
              modalId: ModalType.EditFoodListModal,
              modalData: {
                foodList: foodLists?.['other_diets'],
                onSave: (foodList) => onFoodChange('other', foodList),
                onClose: onCloseModal,
                initialSelectedFood: selectedOther,
                title: 'Other Diets',
                subtitle:
                  'These are additional filters you can select to customize your groceries and diet plan. Select all that apply.',
              },
            })
          }
        >
          Edit
        </Button>
      </HStack>
    </>
  );
};
